import { autoinject, transient } from "aurelia-dependency-injection";
import { OnboardingConfigModel, OnboardingConfigLandingPageModel, OnboardingRequiredDocumentTypeModel, OnboardingLandingPage, OnboardingRequestAllowedCountry } from "../../../../../../models/onboarding/onboarding.models";
import { ChildComponent } from "../../../../modules/child-component";
import { Guid } from "../../../../../../utilities/guid";
import { ArrayUtils } from "../../../../../../utilities/arrays";
import { PropertyChangeNotifier, PropertyMetaModel } from "../../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { computedFrom } from "aurelia-binding";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import * as Enumerable from 'linq';
import { OnboardingNavigationHandlers } from "../../../../onboarding-client/onboarding-navigation-handlers";
import { NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { OnboardingAdminService } from "../../../../../../services/onboarding/onboarding-admin.service";
import { LookupControl } from "../../../../../components/lookups/lookup-control/lookup-control";
import { FileHelper } from "../../../../../../helpers/file-helper";
import { AttachmentsHelper } from "../../../../../../utilities/attachments-helper";
import { DialogServiceConstants } from "../../../../../../constants/basics/dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Router } from "aurelia-router";
import { GenesisRouterConstants } from "../../../../../../configuration/router.constants";
import { Reflection } from "../../../../../../utilities/reflection";
import { useView } from "aurelia-framework";

@autoinject()
@transient()
@useView('views/pages/admin/onboarding/steps/onboarding-plugin-config/onboarding-plugin-config.html')
export class OnboardingPluginConfig extends ChildComponent<OnboardingConfigModel>
{

    public attachmentsVms: LookupControl[] = [];
    public landingPages: string[];
    public pluginLookup: LookupControl;
    public completePagesByLandingPage: any = {};
    public completeRequestLandingPages: string[];
    public landingPagesAdded: string[];
    public completeRequestLandingPagesAdded: any = {};
    public documentRequiredNotifier = new PropertyChangeNotifier();
    public allLandingPagesCombos: { landingPage: string, completeRequestLandingPage: string }[] = [];
    public navigationHandlers: any = OnboardingNavigationHandlers;
    public onboardingRequiredDocumentTypeFiles: any = {};
    public pluginConfigMetaPath: string;
    public variables: PropertyMetaModel[];

    @computedFrom(Reflection.getPropertyPath<OnboardingPluginConfig>(x => x.documentRequiredNotifier.state))
    get requiredDocumentTypesIds() {
        return this.item ? this.item.onboardingRequiredDocumentTypes.map(x => x.attachmentTypeId) : [];
    }

    constructor(
        private onboardingAdminService: OnboardingAdminService,
        private dialogClientService: DialogClientService,
        private router: Router,
        notificationService: NotificationClientService,
        i18N: I18N) {
        super(notificationService, i18N);
    }

    activate(model: { item: OnboardingConfigModel, landingPages: OnboardingLandingPage[], pluginConfigMetaPath: string, variables: PropertyMetaModel[] }) {
        super.activate(model.item);
        this.variables = model.variables;
        this.onboardingRequiredDocumentTypeFiles = {};
        this.pluginConfigMetaPath = model.pluginConfigMetaPath;
        this.prepareLandingPages(model.landingPages);
    }

    private prepareLandingPages(landingPages: OnboardingLandingPage[]) {
        this.landingPages = landingPages.map(x => x.name);
        this.completePagesByLandingPage = Enumerable.from(landingPages).toObject(x => x.name, x => x.completeOnboardingPages);
        setTimeout(() => {
            this.allLandingPagesCombos = Enumerable.from(landingPages).selectMany(p => Enumerable.from(p.completeOnboardingPages), (p, c) => ({ landingPage: p.name, completeRequestLandingPage: c })).toArray();
            if (this.item.landingPages.length == 0) {
                this.addNewLandingPage();
            }
            setTimeout(() => {
                this.onLandingPageSelected();
            });
        });
    }

    onFileChanged($event, requiredDocumentType: OnboardingRequiredDocumentTypeModel) {
        if ($event && !$event.detail.files) {
            requiredDocumentType.templateFilePath = null;
            delete this.onboardingRequiredDocumentTypeFiles[requiredDocumentType.guid];
        } else {
            this.onboardingRequiredDocumentTypeFiles[requiredDocumentType.guid] = $event.detail.files[0];
        }
    }

    selectedWorkflowConfigChanged($event) {
        if ($event.detail && $event.detail.item) {

            const item = $event.detail.item as LookupValue;
            this.item.workflowConfiguration = {
                workflowConfigurationId: item.id,
                guid: item.guid,
                entityType: EntityTypes.WorkflowConfiguration,
                isActive: item.isActive,
                isSelectable: true,
                branchId: item.additionalData['branchId'],
                entityTypeId: item.additionalData['entityTypeId'],
                name: item.name,
                workflowActivities: []
            };
            this.item.workflowConfigurationId = item.id;
        }

        if (this.item.workflowConfigurationId == null && this.item.workflowConfiguration) {
            this.item.workflowConfigurationId = this.item.workflowConfiguration.workflowConfigurationId;
        }

    }

    displayNameFunc = (item: any) => {
        return this.i18N.tr(item.displayName);
    }

    onPluginChanged() {
        if (this.pluginLookup && this.item.onboardingConfigId <= 0 && this.pluginLookup.selectedItem && this.pluginLookup.selectedItem.id > 0) {
            this.onboardingAdminService.getAvailableLandingPages(this.pluginLookup.selectedItem.id).then(x => {
                this.item.landingPages.splice(-1, 0);
                this.prepareLandingPages(x);
            });
        }
    }

    addNewLandingPage() {
        const landingPages = this.item.landingPages;
        const availableCombo = this.allLandingPagesCombos.filter(x => !landingPages.some(z => z.landingPage == x.landingPage && z.completeRequestLandingPage == x.completeRequestLandingPage))[0];
        if (availableCombo) {
            landingPages.push({
                guid: Guid.generateGuid(),
                landingPage: availableCombo.landingPage,
                completeRequestLandingPage: availableCombo.completeRequestLandingPage,
                onboardingConfigId: this.item.onboardingConfigId,
                onboardingConfigLandingPageId: 0,
                entityType: EntityTypes.OnboardingConfigLandingPage,
                identifier: Guid.generateGuid()
            });
            setTimeout(() => {
                this.onLandingPageSelected();
            });
        }
    }

    addRequiredDocumentType() {
        this.item.onboardingRequiredDocumentTypes.push({
            attachmentTypeId: 0,
            onboardingConfigId: this.item.onboardingConfigId,
            onboardingRequiredDocumentTypeId: 0,
            templateFilePath: null,
            guid: Guid.generateGuid(),
            query: null,
            isMandatory: true
        });
    }

    removeRequiredDocumentType(requiredDocumentType: OnboardingRequiredDocumentTypeModel) {
        const index = ArrayUtils.findIndex(this.item.onboardingRequiredDocumentTypes, x => x == requiredDocumentType);
        if (index > -1) {
            this.item.onboardingRequiredDocumentTypes.splice(index, 1);
        }
    }

    removeLandingPage(landingPage: OnboardingConfigLandingPageModel) {
        const index = ArrayUtils.findIndex(this.item.landingPages, x => x.guid == landingPage.guid);
        if (index > -1) {
            this.item.landingPages.splice(index, 1);
            setTimeout(() => {
                this.onLandingPageSelected();
            });
        }
    }

    onDocumentRequiredChanged() {
        this.documentRequiredNotifier.trigger();
    }

    editQuery(requiredDocumentType: OnboardingRequiredDocumentTypeModel) {
        const originalQuery = requiredDocumentType.query;
        const filters = requiredDocumentType.query ? JSON.parse(requiredDocumentType.query) : [];
        this.dialogClientService.show(DialogServiceConstants.URLs.EditQueryBasedOnVariablesDialog, {
            variables: this.variables,
            clauses: filters,
            cancel: () => {
                requiredDocumentType.query = originalQuery;
                this.dialogClientService.hide();
            },
            save: () => {
                requiredDocumentType.query = JSON.stringify(filters);
                this.dialogClientService.hide();
            }
        }, false);
    }

    openClientOnboarding(landingPage: OnboardingConfigLandingPageModel) {
        const win = window.open(`${location.origin}/OnboardingClient/${landingPage.guid}`, '_blank');
        win.focus();
    }

    onLandingPageSelected() {
        this.landingPagesAdded = Enumerable.from(this.item.landingPages).groupBy(x => x.landingPage)
            .where(x => x.count() == this.completePagesByLandingPage[x.key()].length)
            .select(x => x.key())
            .toArray();
        this.completeRequestLandingPagesAdded = Enumerable.from(this.landingPages).toObject(x => x,  () => []);

        Object.keys(this.completeRequestLandingPagesAdded).forEach(landingPage => {
            this.completeRequestLandingPagesAdded[landingPage] = Enumerable.from(this.item.landingPages).where(x => x.landingPage == landingPage).select(x => x.completeRequestLandingPage).toArray();
        });
    }

    configureWorkflow() {
        if (this.item.workflowConfigurationId) {
            this.router.navigateToRoute(GenesisRouterConstants.RouteNames.WorkflowConfiguration, { id: this.item.workflowConfigurationId, entityTypeId: this.pluginLookup.selectedItem.additionalData["entityTypeId"] });
        } else {
            this.router.navigateToRoute(GenesisRouterConstants.RouteNames.WorkflowConfiguration, { entityTypeId: this.pluginLookup.selectedItem.additionalData["entityTypeId"] });
        }

    }

    addNewCountry() {
        this.item.allowedCountries.push({
            countryId: 0,
            onboardingConfigId: this.item.onboardingConfigId
        });
    }

    removeCountry(country: OnboardingRequestAllowedCountry) {
        const index = ArrayUtils.findIndex(this.item.allowedCountries, x => x == country);
        if (index > -1) {
            this.item.allowedCountries.splice(index, 1);
        }
    }

    downloadTemplate(path: string, fileName: string) {
        FileHelper.downloadFile(AttachmentsHelper.getUrl(path), fileName);
    }

}
